/* ShareDocModal — partage d'un document du Drive / Data Room.
 * Interne (membre plateforme) ou externe (lien tokenisé 7j/30j/illimité,
 * option NDA), envoi WhatsApp / email (Resend), liste + révocation.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  X, Link2, Copy, Mail, MessageCircle, Loader2, Ban, ShieldCheck, Eye,
} from "lucide-react";

const EXPIRY_OPTIONS = [
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "unlimited", label: "Illimité (révocable)" },
];

export default function ShareDocModal({ doc, mandateId = null, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [scope, setScope] = useState("external");
  const [expiry, setExpiry] = useState("7d");
  const [requireNda, setRequireNda] = useState(!!mandateId);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [created, setCreated] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/documents/${doc.id}/shares`);
      setShares(r.data?.items || []);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [doc.id]);

  const shareUrl = (token) => `${window.location.origin}/share/${token}`;

  const create = async () => {
    if (scope === "internal" && !email.trim()) {
      notify.error("Email du membre requis pour un partage interne.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(`/workspace/documents/${doc.id}/shares`, {
        scope, expiry, require_nda: requireNda, mandate_id: mandateId,
        target_email: scope === "internal" ? email.trim() : null,
        send_to_email: scope === "external" && email.trim() ? email.trim() : null,
        message,
      });
      setCreated(r.data);
      if (r.data.email_sent) notify.success(`Email envoyé à ${email.trim()}.`);
      else notify.success("Lien de partage créé.");
      setEmail(""); setMessage("");
      await load();
    } catch (e) { notify.error(fmtError(e)); }
    setCreating(false);
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(shareUrl(token));
    notify.success("Lien copié.");
  };

  const openWhatsApp = (token) => {
    const txt = `Je vous partage un document : « ${doc.name} »\n${shareUrl(token)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const sendEmail = async (share) => {
    const to = window.prompt("Email du destinataire :");
    if (!to) return;
    try {
      await api.post(`/workspace/documents/shares/${share.id}/send-email`, { to, message: "" });
      notify.success(`Email envoyé à ${to}.`);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const revoke = async (share) => {
    if (!window.confirm("Révoquer ce lien ? Le destinataire perdra l'accès.")) return;
    try {
      await api.post(`/workspace/documents/shares/${share.id}/revoke`);
      notify.success("Lien révoqué.");
      if (created?.id === share.id) setCreated(null);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal-overlay">
      <div className="bg-[#0D131B] border border-white/10 rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#0D131B]">
          <div className="min-w-0">
            <div className="text-sm text-zinc-100 font-medium flex items-center gap-2"><Link2 className="w-4 h-4 text-[#378ADD]" /> Partager</div>
            <div className="text-[11px] text-zinc-500 truncate">{doc.name}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/5" data-testid="share-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Création */}
          <div className="space-y-3">
            <div className="flex gap-2">
              {[["external", "Lien externe"], ["internal", "Membre plateforme"]].map(([v, l]) => (
                <button key={v} onClick={() => setScope(v)}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors ${scope === v ? "border-[#378ADD] text-white bg-[#378ADD]/10" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}
                  data-testid={`share-scope-${v}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-200" data-testid="share-expiry">
                {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer bg-black/30 border border-white/10 rounded px-2 py-1.5">
                <input type="checkbox" checked={requireNda} onChange={(e) => setRequireNda(e.target.checked)} data-testid="share-nda-toggle" />
                <ShieldCheck className="w-3.5 h-3.5 text-[#1D9E75]" /> NDA requis
              </label>
            </div>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={scope === "internal" ? "Email du membre (obligatoire)" : "Email du destinataire (optionnel — envoi direct)"}
              className="w-full bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-200"
              data-testid="share-email" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
              placeholder="Message personnel (optionnel)"
              className="w-full bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-200"
              data-testid="share-message" />
            <button onClick={create} disabled={creating} className="btn-primary text-xs w-full disabled:opacity-50" data-testid="share-create-btn">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Créer le lien de partage
            </button>
          </div>

          {created && (
            <div className="bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded p-3 space-y-2" data-testid="share-created-box">
              <div className="text-[11px] text-[#1D9E75] font-medium">Lien créé {created.require_nda ? "· NDA requis avant consultation" : ""}</div>
              <div className="text-[11px] font-mono text-zinc-300 break-all bg-black/30 rounded p-2" data-testid="share-created-url">{shareUrl(created.token)}</div>
              <div className="flex gap-2">
                <button onClick={() => copyLink(created.token)} className="btn-secondary text-[11px] flex-1" data-testid="share-copy-btn"><Copy className="w-3 h-3" /> Copier</button>
                <button onClick={() => openWhatsApp(created.token)} className="btn-secondary text-[11px] flex-1 text-[#1D9E75]" data-testid="share-whatsapp-btn"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
                <button onClick={() => sendEmail(created)} className="btn-secondary text-[11px] flex-1" data-testid="share-email-btn"><Mail className="w-3 h-3" /> Email</button>
              </div>
            </div>
          )}

          {/* Liens existants */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Liens actifs</div>
            {loading ? (
              <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>
            ) : shares.length === 0 ? (
              <div className="text-xs text-zinc-600">Aucun partage pour ce document.</div>
            ) : (
              <div className="space-y-2">
                {shares.map((s) => (
                  <div key={s.id} className={`border rounded p-2 text-[11px] ${s.active ? "border-white/10" : "border-white/5 opacity-50"}`} data-testid={`share-item-${s.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap text-zinc-300">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${s.scope === "internal" ? "bg-[#378ADD]/15 text-[#378ADD]" : "bg-white/5 text-zinc-400"}`}>
                          {s.scope === "internal" ? `Interne · ${s.target_email}` : "Externe"}
                        </span>
                        {s.require_nda && (
                          <span className={`px-1.5 py-0.5 rounded text-[9px] ${s.nda_signed ? "bg-[#1D9E75]/15 text-[#1D9E75]" : "bg-[#EF9F27]/15 text-[#EF9F27]"}`}>
                            {s.nda_signed ? `NDA signé · ${s.nda_signer}` : "NDA en attente"}
                          </span>
                        )}
                        <span className="text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> {s.views}</span>
                        <span className="text-zinc-600">{s.expires_at ? `expire ${s.expires_at.slice(0, 10)}` : "illimité"}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {s.active && (
                          <>
                            <button onClick={() => copyLink(s.token)} className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Copier" data-testid={`share-copy-${s.id}`}><Copy className="w-3 h-3" /></button>
                            <button onClick={() => openWhatsApp(s.token)} className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-[#1D9E75]" title="WhatsApp"><MessageCircle className="w-3 h-3" /></button>
                            <button onClick={() => sendEmail(s)} className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Email"><Mail className="w-3 h-3" /></button>
                            <button onClick={() => revoke(s)} className="p-1 rounded hover:bg-white/5 text-zinc-500 hover:text-red-400" title="Révoquer" data-testid={`share-revoke-${s.id}`}><Ban className="w-3 h-3" /></button>
                          </>
                        )}
                        {!s.active && <span className="text-[9px] text-red-400/70 uppercase">{s.revoked_at ? "révoqué" : "expiré"}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
